import * as React from 'react';
import { useMyEcho } from '../hooks/useMyEcho';
import { useDeleteCreatorProfile } from '@/hooks/useDeleteCreatorProfile';
import { NoEchoState } from '../components/states/NoEchoState';
import { DraftsState } from '../components/states/DraftsState';
import { EchoDashboardState } from '../components/states/EchoDashboardState';
import { TestingState } from '../components/states/TestingState';

/**
 * MyEchoPage Component (Mother)
 *
 * Orchestrates the data loading and determines which view state to render:
 * 1. Loading
 * 2. Echo Dashboard (Active Echo)
 * 3. Testing State (Trained draft, no Echo row yet)
 * 4. Drafts List
 * 5. No Echo (Empty State)
 */
export const MyEchoPage: React.FC = () => {
  const {
    echo,
    drafts,
    trainedDraft,
    isLoading,
    isUpdating,
    updateVisibility,
    updatePricing,
    saveEcho,
    retrain,
  } = useMyEcho();
  const { deleteCreatorProfile, isDeleting } = useDeleteCreatorProfile();

  // 0. Loading State
  if (isLoading) {
    return (
      <div className='flex size-full items-center justify-center'>
        <div className='text-brand-800'>Loading your echo...</div>
      </div>
    );
  }

  // Find the published draft linked to this echo (for retrain)
  const publishedDraft = echo ? (drafts.find((draft) => draft.echo_id === echo.id) ?? null) : null;

  // 1. Published Echo -> Dashboard
  if (echo) {
    return (
      <EchoDashboardState
        echo={echo}
        draftId={publishedDraft?.id ?? null}
        isUpdating={isUpdating}
        onUpdateVisibility={updateVisibility}
        onUpdatePricing={updatePricing}
        onRetrain={
          publishedDraft
            ? () => retrain(publishedDraft.id)
            : () => {
                /* no draft */
              }
        }
        onDelete={deleteCreatorProfile}
        isDeleting={isDeleting}
      />
    );
  }

  // 2. Trained draft (persona ready, not yet saved as Echo) -> Testing State
  if (trainedDraft) {
    return (
      <TestingState
        draft={trainedDraft}
        onRetrain={() => retrain(trainedDraft.id)}
        onSaveEcho={(isPublic) => saveEcho(trainedDraft.id, isPublic)}
        isSaving={isUpdating}
      />
    );
  }

  // 3. Drafts exist but none trained -> Drafts List
  if (drafts.length > 0) {
    return <DraftsState drafts={drafts} />;
  }

  // 4. Nothing -> Prompt Creation
  return <NoEchoState />;
};

export default MyEchoPage;
